import { encode, decode } from "@knockout-game/engine";
import EventBus from "@/services/eventBus";
import { useMainStore, useGameStore } from "@/store";

export default class WebTransportProvider {
    static socket = null;
    // True when we closed the socket on purpose (match over / navigated away), so
    // the close handler must NOT treat it as a failure and start reconnecting.
    static intentionalClose = false;

    /**
     * Connect to the gameserver via WebSocket with binary msgpack encoding.
     * Retries up to 5 times with exponential backoff if the connection fails,
     * to handle the race where connection details arrive before the gameserver
     * is ready to accept connections.
     *
     * Authentication is performed via a post-connection handshake: the token
     * is sent as the first binary message after the socket opens (rather than
     * in the URL path) to avoid token exposure in proxy/access logs.
     */
    static connect(hostname, port, token, session, _attempt = 1) {
        const maxAttempts = 5;
        const baseDelay = 600; // ms

        // A fresh connect attempt is, by definition, not an intentional close.
        // Reset on the first attempt so a prior close() doesn't suppress retries
        // for a genuinely new connection.
        if (_attempt === 1) this.intentionalClose = false;

        return new Promise((resolve, reject) => {
            const url = `wss://${hostname}:${port}`;
            const socket = new WebSocket(url);
            socket.binaryType = 'arraybuffer';
            this.socket = socket;
            
            socket.addEventListener('open', () => {
                // Send auth as the first message (post-connection handshake)
                const authMsg = encode({ type: 'auth', token, session }, 'msgpack');
                socket.send(authMsg);
            });

            // Wait for auth-ack/auth-nak before considering the connection ready
            const onAuthResponse = (event) => {
                const data = decode(event.data, 'msgpack');
                if (data.type === 'auth-ack') {
                    socket.removeEventListener('message', onAuthResponse);
                    this.listenForMessages();
                    resolve();
                } else if (data.type === 'auth-nak') {
                    socket.removeEventListener('message', onAuthResponse);
                    socket.close();
                    reject(new Error(data.reason || 'auth_failed'));
                }
            };
            socket.addEventListener('message', onAuthResponse);
            
            socket.addEventListener('close', () => {
                socket.removeEventListener('message', onAuthResponse);
                // Intentional close (match over / navigated away): do not retry.
                if (this.intentionalClose) {
                    resolve();
                    return;
                }
                if (_attempt < maxAttempts) {
                    const delay = baseDelay * Math.pow(2, _attempt - 1);
                    setTimeout(() => {
                        this.connect(hostname, port, token, session, _attempt + 1)
                            .then(resolve)
                            .catch(reject);
                    }, delay);
                } else {
                    EventBus.emit('disconnect');
                    reject();
                }
            });
            
            socket.addEventListener('error', () => {
                // Error is always followed by close; retry logic is in the close handler
            });
        });
    }

    /**
     * Listen for incoming binary messages, decode from msgpack, and route.
     */
    static listenForMessages() {
        this.socket.addEventListener('message', (event) => {
            const data = decode(event.data, 'msgpack');
            
            switch (data.type) {
                case 'event-batch':
                    // Iterate events and emit each individually
                    if (data.events) {
                        for (const gameEvent of data.events) {
                            EventBus.emit(gameEvent.type, gameEvent);
                            useGameStore().ADD_LOG_ENTRY(gameEvent);
                        }
                    }
                    break;
                    
                case 'action-ack':
                    EventBus.emit('action-ack', data);
                    break;
                    
                case 'action-nak':
                    EventBus.emit('action-nak', data);
                    break;
                    
                // Lifecycle messages
                case 'request-loadout':
                    EventBus.emit('request-loadout', data.data.timer);
                    break;
                case 'bout-participants':
                    EventBus.emit('bout-participants', data.data);
                    break;
                case 'bout-starting':
                    EventBus.emit('bout-starting', data.data);
                    break;
                case 'bout-start':
                    EventBus.emit('bout-start');
                    break;
                case 'bout-complete': {
                    const character = useMainStore().characters.find(c => c.id === data.data.character_id);
                    useGameStore().SET_OUTCOME(data.data.outcome);
                    useGameStore().SET_WINNER(data.data.winner);
                    if (character !== undefined) {
                        useGameStore().SET_MMR_CHANGE(data.data.mmr - character.mmr);
                    }
                    useMainStore().SET_CHARACTER_MMR({
                        character_id: data.data.character_id,
                        mmr: data.data.mmr,
                    });
                    if (data.data.refund) {
                        useMainStore().UPDATE_TICKETS(1);
                    }
                    if (useGameStore().source !== 'guild-challenge') {
                        if (data.data.winner) {
                            useMainStore().win_streak++;
                        } else {
                            useMainStore().win_streak = 0;
                        }
                    }
                    useMainStore().processRewards(data.data.rewards.rewards)
                        .then(() => {
                            useGameStore().SET_REWARDS(data.data.rewards.rewards);
                            EventBus.emit('bout-complete', data.data);
                        });
                    break;
                }
                default:
                    // Other game events (player-damage, player-heal, etc.)
                    if (data.type) {
                        EventBus.emit(data.type, data);
                        useGameStore().ADD_LOG_ENTRY(data);
                    }
                    break;
            }
        });
    }

    /**
     * Send a message to the server as binary msgpack.
     * Used for player-action messages (via prediction engine).
     */
    static send(message) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
        const bytes = encode(message, 'msgpack');
        this.socket.send(bytes);
    }

    /**
     * Send a control/lifecycle message to the server as binary msgpack.
     * Used for player-ready, player-forfeit, player-action-release.
     */
    static sendControl(message) {
        // With WebSocket, control and action messages go on the same connection
        this.send(message);
    }

    /**
     * Close the WebSocket connection.
     */
    static close() {
        // Mark the closure as intentional so the socket's close handler does not
        // start the reconnect/backoff loop (which surfaced as repeated
        // "WebSocket connection failed" errors after a match ended).
        this.intentionalClose = true;
        if (this.socket) {
            this.socket.close();
            this.socket = null;
        }
    }
}
